import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { listPlatformPages, type PlatformPageSummary } from "@/lib/admin-api";

import styles from "./pages-manager.module.css";

/**
 * The platform-static page list (ADR-025).
 *
 * The catalogue is fixed — one entry per `PAGE_SURFACE_PLATFORM_*` value — so
 * there is no create action and no empty state to design for. What varies is
 * whether a page has been authored yet, which is the one question this screen
 * exists to answer while the hardcoded Platform routes are still live.
 */
export function PagesManagerPage() {
  const [pages, setPages] = useState<PlatformPageSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    listPlatformPages()
      .then((data) => {
        if (cancelled) return;
        setPages(data.pages ?? []);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message ?? "Failed to load pages");
        setPages([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Pages</h1>
      <p className={styles.lede}>
        BTL's own legal and corporate pages. Editing one here does not change the live route until
        it has been cut over.
      </p>

      {loading ? <p className={styles.notice}>Loading pages…</p> : null}
      {error ? <p className={styles.error}>{error}</p> : null}

      {!loading && !error ? (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Page</th>
              <th>Route</th>
              <th>Status</th>
              <th>Last edited</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {pages.map((page) => (
              <tr key={page.slug}>
                <td className={styles.pageName}>{page.title}</td>
                <td className={styles.route}>{page.route}</td>
                <td>
                  <span className={page.isAuthored ? styles.authored : styles.hardcoded}>
                    {page.isAuthored ? "Authored" : "Not authored"}
                  </span>
                </td>
                <td className={styles.updated}>{formatUpdatedAt(page.updatedAt)}</td>
                <td className={styles.actions}>
                  <Link className={styles.edit} to={`/pages/${page.slug}`}>
                    Edit
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

export function formatUpdatedAt(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
